/**
 * googleIdentity — Google 로그인 팝업(Google Identity Services, code 모델)으로 승인 코드 받기.
 *
 * 브라우저는 **승인 코드만** 받는다. 코드 → 갱신 토큰 교환은 서버(googleCalendarOp "link")가
 * 클라이언트 비밀값으로 한다(redirect_uri = "postmessage"). 토큰은 브라우저로 오지 않는다.
 * 스크립트는 처음 연결할 때만 불러온다.
 */
import { GOOGLE_CALENDAR_SCOPES, GOOGLE_OAUTH_CLIENT_ID } from "@/domain/googleCalendar";

interface CodeResponse { code?: string; error?: string; error_description?: string }
interface CodeClient { requestCode(): void }
interface GisOAuth2 {
  initCodeClient(cfg: {
    client_id: string;
    scope: string;
    ux_mode: "popup";
    select_account?: boolean;
    callback: (r: CodeResponse) => void;
    error_callback?: (e: { type?: string; message?: string }) => void;
  }): CodeClient;
}
declare global {
  interface Window { google?: { accounts?: { oauth2?: GisOAuth2 } } }
}

const GIS_SRC = "https://accounts.google.com/gsi/client";
let loading: Promise<GisOAuth2> | null = null;

function loadGis(): Promise<GisOAuth2> {
  const ready = window.google?.accounts?.oauth2;
  if (ready) return Promise.resolve(ready);
  if (loading) return loading;
  loading = new Promise<GisOAuth2>((resolve, reject) => {
    const s = document.createElement("script");
    s.src = GIS_SRC;
    s.async = true;
    s.onload = () => {
      const o = window.google?.accounts?.oauth2;
      if (o) resolve(o);
      else reject(new Error("Google 로그인 도구를 불러오지 못했습니다."));
    };
    s.onerror = () => {
      loading = null;
      reject(new Error("Google 로그인 도구를 불러오지 못했습니다. 네트워크를 확인하세요."));
    };
    document.head.appendChild(s);
  });
  return loading;
}

/** 팝업을 띄워 승인 코드를 받는다. 사용자가 닫거나 거부하면 throw */
export async function requestGoogleCalendarCode(): Promise<string> {
  const oauth2 = await loadGis();
  return new Promise<string>((resolve, reject) => {
    const client = oauth2.initCodeClient({
      client_id: GOOGLE_OAUTH_CLIENT_ID,
      scope: GOOGLE_CALENDAR_SCOPES,
      ux_mode: "popup",
      select_account: true,
      callback: (r) => {
        if (r.code) resolve(r.code);
        else reject(new Error(r.error === "access_denied" ? "Google 연결을 취소했습니다." : `Google 연결 실패: ${r.error ?? "알 수 없음"}`));
      },
      error_callback: (e) => {
        if (e.type === "popup_closed") reject(new Error("Google 창을 닫아 연결하지 않았습니다."));
        else if (e.type === "popup_failed_to_open") reject(new Error("팝업이 차단되었습니다. 이 사이트의 팝업을 허용하세요."));
        else reject(new Error(e.message || "Google 연결에 실패했습니다."));
      },
    });
    client.requestCode();
  });
}
